import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import {
  DEFAULT_AGGREGATION_CONFIG,
  getAggregationBinConfigParams,
  migrateAggregationBinConfig,
  nextAggregationBinSignalToAggregate,
  toAggregationBinStoreItem,
  validateAggregationConfig,
} from '@/tools/histogram/aggregationBin.utilities';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState } from '@/tools/tool.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { StoredStatistic } from '@/tools/StatisticSelector.molecule';
import type { AggregationConfigState, AggregationTimeBucket, Item } from '@/tools/histogram/aggregationBin.utilities';
import type { BaseToolPersistedState, ParameterDefinitions, ToolItem, ToolRehydratePayload } from '@/tools/tool.types';

export type { AggregationConfigState, AggregationTimeBucket, Item };

export interface AggregationBinState extends BaseToolPersistedState {
  /** The signal or condition selected to aggregate, or `undefined` when none is selected. */
  signalToAggregate: ToolItem | undefined;
  /** The statistic used to create the aggregation. */
  stat: StoredStatistic;
  /** True if empty buckets should be included in the result. */
  includeEmptyBuckets: boolean;
  /** One entry per "Select aggregation type" section of the form. */
  aggregationConfigs: AggregationConfigState[];
}

export type AggregationBinDehydratedState = AggregationBinState;

export interface AggregationBinActions {
  /** Sets the statistic used to create the aggregation. */
  setStat: (stat: StoredStatistic) => void;
  /** Sets whether empty buckets should be included in the result. */
  setEmptyBuckets: (emptyBucketsAllowed: boolean) => void;
  /** Adds an entry to `aggregationConfigs`, assigning it a unique id. */
  addAggregation: () => void;
  /** Removes the aggregation config with the given id. */
  removeAggregation: (id: number) => void;
  /**
   * Merges the given changes into the aggregation config with the given id, resolving the item and re-validating
   * the entry. Does nothing if no entry with that id exists.
   */
  updateAggregationConfig: (
    id: number,
    changes: Partial<Omit<AggregationConfigState, 'id' | 'item'>> & { item?: ToolItem | Item | null },
  ) => void;
  /** Sets the signal or condition to aggregate. */
  setSignalToAggregate: (item: ToolItem | { id: string } | string | null | undefined) => void;
  /** Sets the user-visible name of the output item. */
  setSearchName: (name: string) => void;
  /** Rehydrates the store from a persisted tool-for-edit payload. */
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  /** Resets the store to its initial state, applying any supplied overrides on top of it. */
  reset: (overrides?: Partial<AggregationBinState>) => void;
}

const parameterDefinitions: ParameterDefinitions = {
  signalToAggregate: { predicate: ['name', 'signalToAggregate'] },
};

const initialState: AggregationBinState = {
  ...BASE_TOOL_COMMON_PROPS,
  signalToAggregate: undefined,
  stat: { key: null, timeUnits: 's' },
  includeEmptyBuckets: true,
  aggregationConfigs: [DEFAULT_AGGREGATION_CONFIG],
};

export const dehydrateAggregationBinState = (state: AggregationBinState): AggregationBinDehydratedState => state;

const aggregationBinConfigParamsSelector = createSelector(
  (state: AggregationBinState) => state,
  getAggregationBinConfigParams,
);

const aggregationBinActionsFactory: ActionsFactory<AggregationBinState, AggregationBinActions> = (
  set,
  _get,
  resetState,
) => ({
  setStat: (stat) =>
    set((draft) => {
      draft.stat = stat;
    }),

  setEmptyBuckets: (emptyBucketsAllowed) =>
    set((draft) => {
      draft.includeEmptyBuckets = emptyBucketsAllowed;
    }),

  addAggregation: () =>
    set((draft) => {
      const maxId = draft.aggregationConfigs.reduce((max, config) => Math.max(max, config.id), 0);
      draft.aggregationConfigs.push({ ...DEFAULT_AGGREGATION_CONFIG, id: maxId + 1 });
      // Empty buckets cannot be included once there is more than one grouping.
      draft.includeEmptyBuckets = false;
    }),

  removeAggregation: (id) =>
    set((draft) => {
      const index = draft.aggregationConfigs.findIndex((config) => config.id === id);
      if (index > -1) {
        draft.aggregationConfigs.splice(index, 1);
      }
      if (draft.aggregationConfigs.length === 1) {
        draft.includeEmptyBuckets = true;
      }
    }),

  updateAggregationConfig: (id, changes) =>
    set((draft) => {
      const index = draft.aggregationConfigs.findIndex((config) => config.id === id);
      if (index === -1) {
        return;
      }

      const { item, ...rest } = changes;
      const original = draft.aggregationConfigs[index];
      const next: AggregationConfigState = { ...original, ...rest };

      if ('item' in changes) {
        next.item = toAggregationBinStoreItem(item ?? undefined);
      } else if (original.mode !== next.mode) {
        delete next.item;
      }

      next.valid = validateAggregationConfig(next);
      draft.aggregationConfigs[index] = next;
    }),

  setSignalToAggregate: (item) =>
    set((draft) => {
      draft.signalToAggregate = nextAggregationBinSignalToAggregate(draft.signalToAggregate, item);
    }),

  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  rehydrateForEdit: (payload) =>
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions,
        currentAdvancedParametersCollapsed: draft.advancedParametersCollapsed,
      });
      const migratedConfig = migrateAggregationBinConfig(rehydrationState.config, rehydrationState.parameters);
      // Merge `initialState` first to reset the store, then overlay the rehydrated values — this is the equivalent
      // of calling `resetState()` but in a single `set()` so subscribers only receive one update.
      Object.assign(draft, initialState, migratedConfig, rehydrationState.selectedParameters, {
        originalParameters: rehydrationState.originalParameters,
      });
    }),

  reset: (overrides) => resetState(overrides),
});

const STORE_NAME = 'AggregationBin';

const [AggregationBinStoreProvider, useAggregationBinStoreInstance, useAggregationBinSelector] = createStore(
  STORE_NAME,
  initialState,
  aggregationBinActionsFactory,
);

export { AggregationBinStoreProvider };

export const useAggregationBinId = () => useAggregationBinSelector((state) => state.id);
export const useAggregationBinName = () => useAggregationBinSelector((state) => state.name);
export const useAggregationBinOriginalParameters = () => useAggregationBinSelector((state) => state.originalParameters);
export const useAggregationBinSignalToAggregate = () => useAggregationBinSelector((state) => state.signalToAggregate);
export const useAggregationBinStat = () => useAggregationBinSelector((state) => state.stat);
export const useAggregationBinIncludeEmptyBuckets = () =>
  useAggregationBinSelector((state) => state.includeEmptyBuckets);
export const useAggregationBinAggregationConfigs = () => useAggregationBinSelector((state) => state.aggregationConfigs);
/** The UI config object that would be persisted with the aggregation-bin calculated item for the current state. */
export const useAggregationBinConfigParams = () => useAggregationBinSelector(aggregationBinConfigParamsSelector);

/** Returns the AggregationBin store actions for updating state. */
export const useAggregationBinActions = () => useAggregationBinStoreInstance().actions;

/**
 * @deprecated For legacy singleton bridge only. Non-React consumers (action files) use the singleton created from
 * this in core.stores.ts. DO NOT use in new code.
 */
export function legacyCreateAggregationBinStore() {
  return createStoreInstance<AggregationBinState, AggregationBinActions>(initialState, aggregationBinActionsFactory, {
    storeName: STORE_NAME,
  });
}

export const usePersistAggregationBinStore = () => {
  const register = useRegisterPersistedStore();
  const store = useAggregationBinStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqAggregationBinStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateAggregationBinState,
    });
  }, [register, store]);
};
